import React, { useRef, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import OrderNotifications from './OrderNotifications';
import SafeAvatar from './SafeAvatar';
import { Link, useLocation } from 'react-router-dom';
import { useMyPrivileges } from '../hooks/useMyPrivileges';

export default function Header({ toggleMobileMenu, handleLogout }) {
  const { user } = useAuth();
  const fullName = user ? `${user.firstName} ${user.lastName}` : 'User';
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const location = useLocation();
  const { hasPrivilege } = useMyPrivileges();
  const canAccessTeamAvailability = hasPrivilege('TEAM_AVAILABILITY');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current && 
        !buttonRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close dropdown when location changes (navigating to new page)
  useEffect(() => {
    setIsDropdownOpen(false);
  }, [location]);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  return (
    <header className="h-16 bg-white border-b px-4 flex items-center justify-between">
      {/* Left Section: Mobile Menu Button */}
      <div className="flex items-center">
        <button className="lg:hidden p-2 text-gray-600" onClick={toggleMobileMenu}>
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      {/* Middle Section: Mobile Logo */}
      <div className="flex-1 flex items-center justify-center">
        {/* Mobile Logo */}
        <div className="lg:hidden flex justify-center">
          <img 
            src={process.env.PUBLIC_URL + '/images/logo-mobile.png'} 
            alt="Sweetgrass Properties" 
            className="h-10"
          />
        </div>
      </div>

      {/* Right Section: User Controls */}
      <div className="flex items-center space-x-4">
        <span className="hidden lg:inline-block text-gray-700">{fullName}</span>
        <span className="hidden lg:inline-block bg-sky-600 text-white text-xs px-2 py-1 rounded-full">{user?.role || 'User'}</span>
        
        {/* Notifications */}
        <OrderNotifications />
        
        {/* Profile Dropdown */}
        <div className="relative">
          <button
            ref={buttonRef}
            onClick={toggleDropdown}
            className="flex items-center space-x-3 hover:bg-gray-50 active:bg-gray-100 rounded-lg px-3 py-3 sm:px-2 sm:py-2 transition-colors"
            aria-label="Open user menu"
            aria-expanded={isDropdownOpen}
            aria-haspopup="true"
          >
            <SafeAvatar
              src={user?.avatarUrl}
              alt="Profile"
              className="w-8 h-8 rounded-full"
              size={32}
              lazy={false} // Don't lazy load header avatar since it's always visible
            />
          </button>

          <div 
            ref={dropdownRef}
            className={`${isDropdownOpen ? 'block' : 'hidden'} absolute right-0 mt-2 w-56 sm:w-48 bg-white rounded-lg shadow-lg border py-2 z-50`}
          >
            {canAccessTeamAvailability && (
              <Link
                to="/dashboard/my-availability"
                className="flex items-center px-4 py-3 sm:py-2 text-base sm:text-sm text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
                onClick={() => setIsDropdownOpen(false)}
              >
                <svg className="w-5 h-5 mr-3 sm:w-4 sm:h-4 sm:mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="font-medium sm:font-normal">My Availability</span>
              </Link>
            )}

            <Link 
              to="/dashboard/user-settings" 
              className="flex items-center px-4 py-3 sm:py-2 text-base sm:text-sm text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
              onClick={() => setIsDropdownOpen(false)}
            >
              <svg className="w-5 h-5 mr-3 sm:w-4 sm:h-4 sm:mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="font-medium sm:font-normal">Account Settings</span>
            </Link>
            
            <div className="border-t border-gray-100 my-1"></div>
            
            <button
              onClick={handleLogout}
              className="flex items-center w-full text-left px-4 py-3 sm:py-2 text-base sm:text-sm text-red-600 hover:bg-red-50 active:bg-red-100 transition-colors"
            >
              <svg className="w-5 h-5 mr-3 sm:w-4 sm:h-4 sm:mr-2 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="font-medium sm:font-normal">Sign out</span>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
